import { useState } from 'react';
import { MaintenanceReport, ChecklistItem } from '../types';
import { formatDate } from '../utils';
import { useTranslation } from 'react-i18next';
import { companyPresets, printLanguages } from '../config';
import { useSettings } from '../contexts/SettingsContext';

interface Props {
    report: MaintenanceReport;
    onClose: () => void;
}

const MaintenanceReportPrintView = ({ report, onClose }: Props) => {
    const { t: tUI, i18n } = useTranslation();
    const { settings } = useSettings();

    // State for print language
    const [printLanguage, setPrintLanguage] = useState(
        report.settings?.printLanguage || settings.printLanguage || 'en'
    );
    // State for custom document number override
    const [customDocumentNumber, setCustomDocumentNumber] = useState('');

    const t = i18n.getFixedT(printLanguage);

    // Map print language to country code for company details
    const langToCountry: Record<string, keyof typeof companyPresets> = {
        fi: 'fi',
        sv: 'se',
        no: 'no',
        da: 'dk', // Danish uses Danish company (Halton A/S)
        pl: 'pl',
        nl: 'nl',
        de: 'de',
        fr: 'fr',
        en: 'gb',
    };

    const company = companyPresets[langToCountry[printLanguage]] || companyPresets[report.settings?.companyCountry || settings.companyCountry];

    // Use custom document number if provided
    const displayDocumentNumber = customDocumentNumber || report.documentNumber;

    const handlePrint = () => {
        window.print();
    };

    const { formData, checklist, generalImages, beforeAfterImages } = report;

    // Helper to safely get types array (handles old reports with single 'type' field)
    const getTypesArray = (): string[] => {
        if (Array.isArray(formData.types)) {
            return formData.types;
        }
        // Fallback for old format
        const oldType = (formData as any).type;
        return oldType ? [oldType] : ['kcw_ww_technical'];
    };
    const typesArray = getTypesArray();

    // Get translated checklist label
    const getChecklistLabel = (item: ChecklistItem): string => {
        if (item.labelKey) {
            return t(item.labelKey);
        }
        return item.label;
    };

    return (
        <div className="print-view">
            {/* Print Controls - Hidden when printing */}
            <div className="print-controls no-print">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        ← {tUI('common.back')}
                    </button>

                    {/* Language Selector */}
                    <select
                        value={printLanguage}
                        onChange={(e) => setPrintLanguage(e.target.value as any)}
                        className="form-select"
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                        title={t('settings.print.language')}
                    >
                        {printLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                                {lang.nativeName}
                            </option>
                        ))}
                    </select>

                    {/* Custom Document Number Input */}
                    <input
                        type="text"
                        value={customDocumentNumber}
                        onChange={(e) => setCustomDocumentNumber(e.target.value)}
                        placeholder={t('print.header.documentNumber')}
                        className="form-input"
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '140px' }}
                    />
                </div>

                <button className="btn btn-primary" onClick={handlePrint}>
                    🖨️ {tUI('common.printPdf')}
                </button>
                <span className="print-tip" style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '12px' }}>
                    💡 {t('print.tip.saveasPdf')}
                </span>
            </div>

            <div className="print-document maintenance-report-document">
                {/* Header */}
                <header className="print-header">
                    <div className="print-header-left">
                        <img src="/halton-logo.png" alt="Halton" className="print-logo" style={{ height: '32px', marginBottom: '8px' }} />
                        <div className="company-info">
                            {company.name}<br />
                            {company.address}<br />
                            {company.postalCode} {company.city}<br />
                            {t('print.company.vat')}: {company.vatId}
                        </div>
                    </div>

                    <div className="print-header-right">
                        <h1 className="document-title">{t('maintenance.report.title')}</h1>
                        <div className="document-meta">
                            <div className="meta-row">
                                <span className="meta-label">{t('print.header.documentNumber')}:</span>
                                <span className="meta-value">{displayDocumentNumber}</span>
                            </div>
                            <div className="meta-row">
                                <span className="meta-label">{t('print.header.date')}:</span>
                                <span className="meta-value">{formatDate(formData.date, printLanguage)}</span>
                            </div>
                            <div className="meta-row">
                                <span className="meta-label">{t('maintenance.report.type')}:</span>
                                <span className="meta-value">{typesArray.map(type => t(`maintenance.types.${type}`)).join(', ')}</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Customer & Site Information */}
                <section className="print-section" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('print.customer.title')}</h2>
                        <div className="customer-info">
                            {formData.customerName && <strong>{formData.customerName}</strong>}
                            {formData.customerAddress && <span>{formData.customerAddress}</span>}
                            {formData.contactPerson && <span>{t('print.customer.contact')}: {formData.contactPerson}</span>}
                            {formData.contactPhone && <span>{t('print.customer.phone')}: {formData.contactPhone}</span>}
                        </div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                        <h2 className="print-section-title">{t('maintenance.general.siteName')}</h2>
                        <div className="customer-info">
                            {formData.siteName && <strong>{formData.siteName}</strong>}
                            {formData.siteAddress && <span>{formData.siteAddress}</span>}
                            <span>{t('maintenance.general.technician')}: {formData.technician}</span>
                        </div>
                    </div>
                </section>

                {/* Control Centers */}
                {formData.controlCenters && formData.controlCenters.filter(cc => cc.type !== 'none').length > 0 && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('maintenance.controlCenters.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '10%' }}>#</th>
                                    <th style={{ width: '50%' }}>{t('maintenance.controlCenters.type')}</th>
                                    <th style={{ width: '40%' }}>{t('maintenance.controlCenters.serialNumber')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.controlCenters.filter(cc => cc.type !== 'none').map((cc, idx) => (
                                    <tr key={cc.id}>
                                        <td>{idx + 1}</td>
                                        <td>{cc.type === 'custom' ? (cc.customName || t('maintenance.controlCenters.types.custom')) : t(`maintenance.controlCenters.types.${cc.type}`)}</td>
                                        <td>{cc.type === 'custom' ? (cc.customDescription || '-') : (cc.serialNumber || '-')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Hoods / Equipment */}
                {formData.hoods && formData.hoods.length > 0 && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('maintenance.hoods.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '20%' }}>{t('maintenance.hoods.name')}</th>
                                    <th style={{ width: '15%' }}>{t('maintenance.hoods.location')}</th>
                                    <th style={{ width: '30%' }}>{t('maintenance.hoods.technologies')}</th>
                                    <th style={{ width: '15%' }}>{t('maintenance.hoods.uvHours')}</th>
                                    <th style={{ width: '15%' }}>{t('maintenance.hoods.notes')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.hoods.map((hood, idx) => (
                                    <tr key={hood.id}>
                                        <td>{idx + 1}</td>
                                        <td><strong>{hood.name}</strong></td>
                                        <td>{hood.location || '-'}</td>
                                        <td>
                                            {hood.technologies.length > 0
                                                ? hood.technologies.map(tech => t(`maintenance.technologies.${tech}`)).join(', ')
                                                : '-'}
                                        </td>
                                        <td>
                                            {hood.technologies.includes('uv') && hood.uvHours
                                                ? `${hood.uvHours} h`
                                                : '-'}
                                        </td>
                                        <td style={{ fontSize: '0.85em' }}>{hood.notes || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Mobichef Pressure Readings */}
                {typesArray.includes('mobichef') && formData.mobichefPressure && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('maintenance.mobichef.pressureReadings')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th>{t('maintenance.mobichef.pressureLow')}</th>
                                    <th>{t('maintenance.mobichef.pressureMedium')}</th>
                                    <th>{t('maintenance.mobichef.pressureHigh')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ textAlign: 'center', fontSize: '1.2em', fontWeight: 'bold' }}>
                                        {formData.mobichefPressure.low ?? '-'} {t('maintenance.mobichef.pressureUnit')}
                                    </td>
                                    <td style={{ textAlign: 'center', fontSize: '1.2em', fontWeight: 'bold' }}>
                                        {formData.mobichefPressure.medium ?? '-'} {t('maintenance.mobichef.pressureUnit')}
                                    </td>
                                    <td style={{ textAlign: 'center', fontSize: '1.2em', fontWeight: 'bold' }}>
                                        {formData.mobichefPressure.high ?? '-'} {t('maintenance.mobichef.pressureUnit')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Checklist */}
                {checklist && checklist.length > 0 && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('maintenance.checklist.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '55%' }}>{t('print.pricing.description')}</th>
                                    <th style={{ width: '15%', textAlign: 'center' }}>{t('maintenance.checklist.status')}</th>
                                    <th style={{ width: '25%' }}>{t('maintenance.checklist.notes')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checklist.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td>{idx + 1}</td>
                                        <td>{getChecklistLabel(item)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`checklist-status ${item.status === 'OK' ? 'status-ok' : item.status === 'NOK' ? 'status-nok' : item.status === 'NA' ? 'status-na' : 'status-empty'}`}>
                                                {item.status || '-'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '0.85em' }}>{item.notes || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* General Notes */}
                {formData.generalNotes && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('maintenance.mobichef.notes')}</h2>
                        <div className="description-box">
                            {formData.generalNotes}
                        </div>
                    </section>
                )}

                {/* Parts & Out of Contract Work */}
                {(formData.partsAndConsumables || formData.outOfContractWork) && (
                    <section className="print-section" style={{ display: 'flex', gap: '30px' }}>
                        {formData.partsAndConsumables && (
                            <div style={{ flex: 1 }}>
                                <h2 className="print-section-title">{t('maintenance.parts.partsUsed')}</h2>
                                <div className="description-box">
                                    {formData.partsAndConsumables}
                                </div>
                            </div>
                        )}
                        {formData.outOfContractWork && (
                            <div style={{ flex: 1 }}>
                                <h2 className="print-section-title">{t('maintenance.parts.outOfContract')}</h2>
                                <div className="instruction-block instruction-warning" style={{ whiteSpace: 'pre-wrap' }}>
                                    {formData.outOfContractWork}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Before/After Images */}
                {beforeAfterImages && beforeAfterImages.length > 0 && (
                    <section className="print-section print-images-section">
                        <h2 className="print-section-title">{t('maintenance.images.beforeAfter')}</h2>
                        <div style={{ display: 'grid', gap: '20px' }}>
                            {beforeAfterImages.map((pair, idx) => (
                                <div key={pair.id} className="image-pair">
                                    <div>
                                        <p className="image-label">#{idx + 1} - {t('maintenance.images.before')}</p>
                                        {pair.before ? (
                                            <div className="image-container">
                                                <img src={pair.before} alt="Before" />
                                            </div>
                                        ) : (
                                            <div style={{ height: '150px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                No image
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p className="image-label">#{idx + 1} - {t('maintenance.images.after')}</p>
                                        {pair.after ? (
                                            <div className="image-container">
                                                <img src={pair.after} alt="After" />
                                            </div>
                                        ) : (
                                            <div style={{ height: '150px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                No image
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* General Images */}
                {generalImages && generalImages.length > 0 && (
                    <section className="print-section print-images-section">
                        <h2 className="print-section-title">{t('maintenance.images.generalImages')}</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                            {generalImages.map((img, idx) => (
                                <div key={idx} style={{ pageBreakInside: 'avoid' }}>
                                    <div className="image-container">
                                        <img src={img} alt={`Image ${idx + 1}`} />
                                    </div>
                                    <p className="image-caption">{t('maintenance.images.count')} {idx + 1}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Signatures */}
                <section className="signature-section">
                    <h3 className="section-title">{t('maintenance.report.signatures')}</h3>
                    <div className="signature-boxes">
                        <div className="signature-box">
                            <div className="signature-box-title">{t('maintenance.general.technician')}</div>
                            <div className="signature-line"></div>
                            <div className="signature-label">
                                {t('maintenance.general.technician')}
                            </div>
                            <div className="signature-name">{formData.technician}</div>
                            <div className="signature-name">{formatDate(formData.date, printLanguage)}</div>
                        </div>
                        <div className="signature-box">
                            <div className="signature-box-title">{t('maintenance.report.customerSignature')}</div>
                            <div className="signature-line"></div>
                            <div className="signature-label">
                                {t('maintenance.report.customerSignature')}
                            </div>
                            <div className="signature-name">{formData.contactPerson || formData.customerName}</div>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="print-footer">
                    <div className="print-footer-left">
                        <strong>{displayDocumentNumber}</strong>
                        <span style={{ marginLeft: '16px' }}>{typesArray.map(type => t(`maintenance.types.${type}`)).join(', ')}</span>
                    </div>
                    <div className="print-footer-right">
                        {formatDate(report.createdAt, printLanguage)} | Halton Service
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default MaintenanceReportPrintView;
